import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonLabel,
  IonModal,
  IonNote,
  IonRow,
  IonSegment,
  IonSegmentButton,
  IonText,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import React, { memo, useEffect, useState, Fragment } from "react";
import { isEmpty } from "underscore";
import { v4 as uuidv4 } from "uuid";
import { QUICKSTRIKE_PRICECHART_URL } from "./Constants";
import config from "../../../components/helpers/socket/config";
import { pubsubEvents } from "../../../components/helpers/socket/pubsubEvent";
import OneLoader from "../../../utils/component-utils/Loader/OneLoader";
import { hapticsImpactMedium } from "../../../utils/core-utils/hapticUtils";
import { send } from "../../../utils/sockets/asyncSocket";
import {
  setCapacitorPreference,
  removeCapacitorPreference,
} from "../../../utils/core-utils/capPreferencesUtils";
import { CapacitorPreferencesConstants } from "../../../components/Constants";
import { CMEDfirebaseLogEvent } from "../../../utils/firebaseAnalytics/firebaseUtils";
import firebaseEventConfig from "../../../utils/firebaseAnalytics/firebaseEventConfig";
import { closeOutline } from "ionicons/icons";
import Skeleton__MarketDataOverviewModal from "./Skeleton__MarketDataOverviewModal";
import { getModifiedContractNameFromOrder2 } from "../../../utils/commonUtils";
import { OrdersDataType, OrdersType } from "../../../Constants";

interface MarketDataOverviewModalProps {
  isMarketDataOverviewModalOpen: boolean | undefined;
  setIsMarketDataOverviewModalOpen: Function;
  setOpenOrderEntryModal: Function;
  setOrderEntryType: Function;
  setOrderEntryData: Function;
  tradingGridGroupData: any;
  orderEntryData_Live: any;
  setOrderEntryData_Live: any;
}

const MarketDataOverviewModal: React.FC<MarketDataOverviewModalProps> = ({
  isMarketDataOverviewModalOpen,
  setIsMarketDataOverviewModalOpen,
  setOpenOrderEntryModal,
  setOrderEntryType,
  setOrderEntryData,
  tradingGridGroupData,
  orderEntryData_Live,
  setOrderEntryData_Live,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentSegment, setCurrentSegment] = useState<string>("overview");
  const [marketDataDepth, setMarketDataDepth] = useState<{
    data: any[];
    loading: boolean;
  }>({ data: [], loading: true });
  const [trendSpiderChartURL, setTrendSpiderChartURL] = useState<string>("");
  const [contractName, setContractName] = useState<{
    name: string;
    loading: boolean;
  }>({ name: "", loading: true });

  let subRequestForContractMarketData = {
    action: config.socket.actions.CMED_ORDERS_SUBSCRIBE,
    type: OrdersType.CONTRACT_MARKET_DATA,
    requestID: uuidv4(),
    payload: {
      ordersDataType: OrdersDataType.CONTRACT_MARKET_DATA,
      itemId: tradingGridGroupData.ii,
    },
  };

  let unSubRequestForContractMarketData = {
    action: config.socket.actions.CMED_CONTRACT_MD_UNSUBSCRIBE,
    type: OrdersType.CONTRACT_MARKET_DATA,
    requestID: uuidv4(),
    payload: {
      ordersDataType: OrdersDataType.CONTRACT_MARKET_DATA,
      itemId: tradingGridGroupData.ii,
    },
  };

  function updateContractMarketData(data: any, res: any) {
    const payload: any[] = res.payload.data;
    const identifier: string = res.payload.identifier;

    // Check if this message belongs to the current instrument
    if (tradingGridGroupData.ii === identifier) {
      // If it's a snapshot, we handle it and MUST set loading to false
      if (res.payload.isSnapshot) {
        const updatedMarketDataDepth = (payload || [])
          ?.filter((item) => parseInt(item?.id) <= 5)
          ?.map((item) => ({
            id: item.id,
            data: {
              b: item.b,
              bq: item.bq,
              o: item.o,
              oq: item.oq,
            },
          }));

        setMarketDataDepth({ data: updatedMarketDataDepth, loading: false });
        return; // Snapshot handled
      }

      // For incremental updates, if there's no data to process,
      // just ensure loading is false and exit.
      if (isEmpty(res.payload.data) && isEmpty(res.payload.deletions)) {
        setMarketDataDepth((prev) => ({ ...prev, loading: false }));
        return;
      }

      // Handle incremental updates and deletions
      setMarketDataDepth((prevMarketDataDepth) => {
        const updatedMarketData = [...prevMarketDataDepth.data];

        payload.forEach((item) => {
          if (parseInt(item?.id) <= 5) {
            const existingItemIndex = updatedMarketData.findIndex(
              (existingItem) => existingItem?.id === item?.id,
            );
            if (existingItemIndex !== -1) {
              updatedMarketData[existingItemIndex] = {
                id: item?.id,
                data: { b: item?.b, bq: item?.bq, o: item?.o, oq: item?.oq },
              };
            } else {
              updatedMarketData.push({
                id: item?.id,
                data: { b: item?.b, bq: item?.bq, o: item?.o, oq: item?.oq },
              });
            }
          }
        });

        res?.payload?.deletions?.forEach((deletedId: any) => {
          if (parseInt(deletedId) <= 5) {
            const deletedItemIndex = updatedMarketData.findIndex(
              (existingItem) => existingItem?.id === deletedId,
            );
            if (deletedItemIndex !== -1)
              updatedMarketData?.splice(deletedItemIndex, 1);
          }
        });

        return { data: updatedMarketData, loading: false };
      });
    }
  }

  function processDate(dateStr: string): string {
    const monthMapping: { [key: string]: string } = {
      Jan: "F",
      Feb: "G",
      Mar: "H",
      Apr: "J",
      May: "K",
      Jun: "M",
      Jul: "N",
      Aug: "Q",
      Sep: "U",
      Oct: "V",
      Nov: "X",
      Dec: "Z",
    };
    const match = dateStr.match(/(?:(\d{2}))?([A-Za-z]{3})(\d{2})/);
    if (!match) {
      console.error("Invalid date input format:", dateStr);
      return "";
    }
    const month = match[2];
    const yearSuffix = match[3];
    const convertedMonth = monthMapping[month] || "";
    const lastDigitOfYear = yearSuffix.slice(-1);
    return convertedMonth + lastDigitOfYear;
  }

  function formatButterflySpread(prefix: string, dateParts: string[]): string {
    const processedDates = dateParts.map(processDate);
    return `${prefix}:BF ${processedDates.join("-")}`;
  }

  function formatPeriodSpread(prefix: string, dateParts: string[]): string {
    const processedDates = dateParts.map(processDate);
    return `${prefix}${processedDates[0]}-${prefix}${processedDates[1]}`;
  }

  function formatOutright(prefix: string, dateParts: string[]): string {
    /*
    Normally period spreads aren't listed out that far -
    but some outrights are listed out far enough
    where it's not enough detail to have a single digit
    for the year - like 5, since that could be 2025 or 2035
    and hence we use 2 digits for the year like 25 or 35
    */
    const twoDigitYearPrefixes = [
      "ASPR",
      "ASPT",
      "ASR",
      "AST",
      "BCX",
      "NG",
      "NNE",
      "NPG",
      "QEF",
      "QNF",
      "QOF",
      "QRF",
      "QSF",
      "QTF",
      "QXF",
      "YIA",
      "YIB",
      "YIC",
      "YID",
      "YIE",
      "YII",
      "YIL",
      "YIO",
      "YIT",
      "YIW",
      "YIY",
    ];

    if (twoDigitYearPrefixes.includes(prefix)) {
      const monthMapping: { [key: string]: string } = {
        Jan: "F",
        Feb: "G",
        Mar: "H",
        Apr: "J",
        May: "K",
        Jun: "M",
        Jul: "N",
        Aug: "Q",
        Sep: "U",
        Oct: "V",
        Nov: "X",
        Dec: "Z",
      };
      const processTwoDigitYearDate = (dateStr: string): string => {
        const match = dateStr.match(/(?:(\d{2}))?([A-Za-z]{3})(\d{2})/);
        if (!match) return "";
        const month = match[2];
        const yearSuffix = match[3];
        const convertedMonth = monthMapping[month] || "";
        return convertedMonth + yearSuffix;
      };
      let result = prefix + processTwoDigitYearDate(dateParts[0]);
      if (dateParts.length > 1) {
        result += "-" + prefix + processTwoDigitYearDate(dateParts[1]);
      }
      return result;
    }
    let result = prefix + processDate(dateParts[0]);
    if (dateParts.length > 1) {
      result += "-" + prefix + processDate(dateParts[1]);
    }
    return result;
  }

  function convertString(inputStr: string): string {
    if (!inputStr || typeof inputStr !== "string") {
      return "";
    }
    const parts = inputStr.split(" ");
    if (parts.length < 2) {
      return inputStr; // Or handle as an error
    }
    const prefix = parts[0];
    const datePart = parts.slice(1).join(" ");
    const dateRange = datePart.split("/");

    const slashCount = (datePart.match(/\//g) || []).length;

    var result;
    if (slashCount === 2 && dateRange.length === 3) {
      // Butterfly Spread
      result = formatButterflySpread(prefix, dateRange);
    } else if (slashCount === 1 && dateRange.length === 2) {
      // Period Spread
      result = formatPeriodSpread(prefix, dateRange);
    } else {
      // Default case (including outright)
      result = formatOutright(prefix, dateRange);
    }
    return result;
  }

  const onSegmentChange = (value: string) => {
    setCurrentSegment(value);
    hapticsImpactMedium();
    if (value === "chart") {
      CMEDfirebaseLogEvent(
        firebaseEventConfig.CMEDComponentNames.CMED_MARKET_DATA,
        firebaseEventConfig.eventNames.CMED_MARKET_DATA
          .CMED_MARKET_DATA_SWITCH_TO_CHART,
      );
    }
  };

  function _getTradeableitemInfo() {
    const TradeableItemId = tradingGridGroupData?.ii;
    send(config.socket.channels.TRADING, {
      action: config.socket.actions.CMED_GET_CONTRACTS_BY_ITEM_ID,
      requestID: uuidv4(),
      type: "notUsedButMustBePresentForLegacyValidation",
      payload: {
        TradeableItemId,
      },
    });
  }

  function _getTrendSpiderChartURL() {
    send(config.socket.channels.CORE, {
      action: "getTrendSpiderChartURL",
      type: "legacyValidation",
      requestID: uuidv4(),
      payload: {
        globexCode: convertString(tradingGridGroupData?.c),
      },
    });
  }

  function _getContractMarketData() {
    send(config.socket.channels.TRADING, subRequestForContractMarketData);
  }

  useEffect(() => {
    if (isMarketDataOverviewModalOpen) {
      PubSub.subscribe(
        pubsubEvents.stompSubscriptionKeys.CONTRACT_MARKET_DATA,
        (msg, data) => {
          updateContractMarketData(msg, data);
        },
      );
      PubSub.subscribe(
        pubsubEvents.socketKeys.GET_TRENDSPIDER_CHART_URL_SUCCESS,
        (msg, data) => {
          const url = data?.payload?.url
            ? decodeURIComponent(data?.payload?.url)
            : "";
          setTrendSpiderChartURL(url || "");
          setIsLoading(false);
        },
      );
      PubSub.subscribe(
        pubsubEvents.socketKeys.CMED_ALL_CONTRACTS_SUCCESS,
        (msg, data) => {
          setContractName({ name: data.payload.Name, loading: false });
        },
      );
      _getTradeableitemInfo();
      _getTrendSpiderChartURL();
      _getContractMarketData();
      setCapacitorPreference(
        CapacitorPreferencesConstants.MARKETDATA_MODAL_INSTRUMENT_ID,
        tradingGridGroupData.ii,
      );
      return () => {
        PubSub.unsubscribe(
          pubsubEvents.stompSubscriptionKeys.CONTRACT_MARKET_DATA,
        );
        PubSub.unsubscribe(
          pubsubEvents.socketKeys.GET_TRENDSPIDER_CHART_URL_SUCCESS,
        );
        // PubSub.unsubscribe(pubsubEvents.socketKeys.CMED_ALL_CONTRACTS_SUCCESS);
      };
    }
  }, [isMarketDataOverviewModalOpen]);

  return (
    <IonModal isOpen={isMarketDataOverviewModalOpen}>
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonTitle>
            <p className="md-modal-header-text">CONTRACT DETAILS</p>
          </IonTitle>
          <IonButtons slot="end">
            <IonButton
              onClick={() => {
                setIsMarketDataOverviewModalOpen(false);
                setIsLoading(true);
                setCurrentSegment("overview");
                send(
                  config.socket.channels.TRADING,
                  unSubRequestForContractMarketData,
                );
                removeCapacitorPreference("MarketDataOverviewModalOpen_insId");
                setContractName({ name: "", loading: true });
                setMarketDataDepth({ data: [], loading: true });
              }}
            >
              <IonIcon
                icon={closeOutline}
                color="dark"
                slot="icon-only"
              ></IonIcon>
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      {contractName.loading || marketDataDepth.loading ? (
        <Skeleton__MarketDataOverviewModal />
      ) : (
        <Fragment>
          <IonSegment
            value={currentSegment}
            onIonChange={(e: any) => onSegmentChange(e.detail.value)}
          >
            <IonSegmentButton value="overview">
              <IonLabel>Overview</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="chart">
              <IonLabel>Chart</IonLabel>
            </IonSegmentButton>
          </IonSegment>
          <section className="md-contract-header">
            <div>
              <div className="md-contract-name-container">
                <p className="md-contract-name">
                  {getModifiedContractNameFromOrder2(
                    contractName.name,
                    tradingGridGroupData,
                  )}
                </p>
              </div>
              <div className="md-contract-symbol-container">
                <p className="md-contract-symbol">{tradingGridGroupData?.c}</p>
              </div>
            </div>
          </section>
          <IonContent>
            {currentSegment === "overview" ? (
              <main className="overview-container">
                <section className="md-depth-section">
                  <div className="md-grid-header-row">
                    {["QTY", "BID", "OFFER", "QTY"].map((elem, id) => (
                      <div
                        key={id}
                        className={`md-grid-header-cell ${
                          id === 1 || id === 3 ? "align-end" : "align-start"
                        }`}
                      >
                        <p className="md-grid-header-text">{elem}</p>
                      </div>
                    ))}
                  </div>
                  {Array.from({ length: 5 }).map((_, index) => {
                    const item = marketDataDepth.data[index];
                    return (
                      <div
                        key={index}
                        className={`md-depth-row ${
                          index % 2 === 0 ? "bg-trans" : "bg-alt"
                        }`}
                      >
                        {[
                          { val: item?.data?.bq, align: "flex-start" },
                          { val: item?.data?.b, align: "flex-end" },
                          { val: item?.data?.o, align: "flex-start" },
                          { val: item?.data?.oq, align: "flex-end" },
                        ].map((col, i) => (
                          <div
                            key={i}
                            className="md-depth-cell"
                            style={{ justifyContent: col.align }}
                          >
                            <p className="md-depth-cell-text">
                              {col.val?.r !== 0 ? col.val?.f : "\u00A0"}
                            </p>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </section>
                <section className="md-stats-section">
                  <div className="md-stats-title-row">
                    <div className="md-stats-title-cell">
                      <p className="md-grid-header-text">MARKET STATISTICS</p>
                    </div>
                  </div>
                  {[
                    {
                      l1: "HIGH",
                      v1: orderEntryData_Live?.h?.f,
                      l2: "VOLUME",
                      v2: orderEntryData_Live?.t?.f,
                    },
                    {
                      l1: "LOW",
                      v1: orderEntryData_Live?.ltp?.f,
                      l2: "CHANGE",
                      v2: orderEntryData_Live?.n?.f,
                      isChange: true,
                    },
                    {
                      l1: "OPEN",
                      v1:
                        orderEntryData_Live?.o?.f || orderEntryData_Live?.ot?.f,
                      l2: "LAST TRADE",
                      v2: orderEntryData_Live?.l?.f,
                    },
                    {
                      l1: "SETTLE",
                      v1: orderEntryData_Live?.sp?.f,
                      l2: "OPEN INTEREST",
                      v2: orderEntryData_Live?.oi?.f,
                    },
                    {
                      l1: "CURRENT SETTLE",
                      v1: orderEntryData_Live?.cs?.f,
                      l2: "NET CHANGE",
                      isNetChange: true,
                      v2: (() => {
                        const n = orderEntryData_Live?.n?.r;
                        const l = orderEntryData_Live?.l?.r;
                        const calc = n / Math.abs(l - n);
                        return !isNaN(calc)
                          ? (calc * 100).toFixed(2) + "%"
                          : "-";
                      })(),
                      color: (() => {
                        const val =
                          orderEntryData_Live?.n?.r /
                          Math.abs(
                            orderEntryData_Live?.l?.r -
                              orderEntryData_Live?.n?.r,
                          );
                        return val > 0 ? "success" : val < 0 ? "danger" : "";
                      })(),
                    },
                  ].map((row, index) => (
                    <div
                      key={index}
                      className={`md-stats-row ${
                        index % 2 === 0 ? "bg-trans" : "bg-alt"
                      }`}
                    >
                      <div className="md-stats-label-cell">
                        <p className="md-stats-label-text">{row.l1}</p>
                      </div>
                      <div className="md-stats-value-cell">
                        <p className="md-stats-value-text">{row.v1 || "-"}</p>
                      </div>
                      <div className="md-stats-label-cell">
                        <p className="md-stats-label-text">{row.l2}</p>
                      </div>
                      <div className="md-stats-value-cell">
                        <p className="md-stats-value-text">
                          {row.isNetChange ? (
                            <IonText color={row.color}>
                              {parseFloat(row.v2) > 0 ? "+" : ""}
                              {row.v2}
                            </IonText>
                          ) : row.isChange ? (
                            <IonText
                              color={
                                orderEntryData_Live?.n?.r < 0
                                  ? "danger"
                                  : orderEntryData_Live?.n?.r > 0
                                    ? "success"
                                    : ""
                              }
                            >
                              {orderEntryData_Live?.n?.r > 0 ? "+" : ""}
                              {row.v2 || "-"}
                            </IonText>
                          ) : (
                            row.v2 || "-"
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </section>
              </main>
            ) : (
              <Fragment>
                {isLoading && (
                  <div className="md-loader-container">
                    <OneLoader loadingText="Loading Chart..." />
                  </div>
                )}
                {!isLoading && !trendSpiderChartURL && (
                  <div className="md-no-chart-container">
                    <IonNote color="medium" className="md-no-chart-text">
                      The chart for this contract is currently unavailable.
                    </IonNote>
                  </div>
                )}
                {trendSpiderChartURL && (
                  <iframe
                    className="md-iframe-chart"
                    style={{ display: isLoading ? "none" : "block" }}
                    src={trendSpiderChartURL}
                    title="TrendSpider Chart"
                    onLoad={() => setIsLoading(false)}
                    onError={() => {
                      setIsLoading(false);
                      setTrendSpiderChartURL("");
                    }}
                  />
                )}
              </Fragment>
            )}
          </IonContent>
          <IonFooter className="ion-no-padding ion-no-border md-footer-container">
            <IonGrid className="ion-padding">
              <IonRow>
                <IonCol size="6">
                  <IonButton
                    className="custom_cmed_mobile_button"
                    expand="block"
                    fill="solid"
                    color="primary"
                    onClick={() => {
                      setOpenOrderEntryModal(true);
                      setOrderEntryType("bid");
                      setOrderEntryData(tradingGridGroupData);
                      setOrderEntryData_Live(tradingGridGroupData);
                      hapticsImpactMedium();
                    }}
                  >
                    NEW BID
                  </IonButton>
                </IonCol>
                <IonCol size="6">
                  <IonButton
                    className="custom_cmed_mobile_button"
                    expand="block"
                    fill="solid"
                    color="danger"
                    onClick={() => {
                      setOpenOrderEntryModal(true);
                      setOrderEntryType("offer");
                      setOrderEntryData(tradingGridGroupData);
                      setOrderEntryData_Live(tradingGridGroupData);
                      hapticsImpactMedium();
                    }}
                  >
                    NEW OFFER
                  </IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonFooter>
        </Fragment>
      )}
    </IonModal>
  );
};

export default memo(MarketDataOverviewModal);
